import React, { useEffect, useMemo, useState } from "react";
import { getUser } from "../../helpers";
import { useHistory } from "react-router-dom";
import { FaTrash } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { createErrorAlert } from "../../redux/actions";

const API_BASE = "https://aibackend.studycrowdai.us/v1";
const AUTH_TOKEN = "Bearer app-RwAMNt12y2YAKTZhyZNBUN0E";

function ChatSidebar({ credits, onProfileClick, onLogoutClick, refreshTrigger }) {
    const [sessions, setSessions] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const dispatch = useDispatch();
    const history = useHistory();

    const user = JSON.parse(getUser());
    const params = new URLSearchParams(window.location.search);
    const userId = params.get("user_id") || params.get("userid");
    const currentSessionId = params.get("session_id");

    useEffect(() => {
        fetchAllSessions();
    }, [userId, refreshTrigger]); // ✅ re-fetch when ChatInterface creates a new session

    const fetchAllSessions = async () => {
        if (!user?.id) return;
        try {
            const res = await fetch(
                `${API_BASE}/conversations?user=${user.id}&limit=50`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: AUTH_TOKEN,
                    },
                }
            );
            const response = await res.json();
            setSessions(response?.data || []);
        } catch (e) {
            console.error("Error fetching sessions:", e);
        }
    };

    const handleSessionClick = (id) => {
        window.location.replace(`/aitutor?user_id=${user?.id}&session_id=${id}`);
    };

    const handleDeleteConversation = async (e, conversationId) => {
        e.stopPropagation();
        try {
            await fetch(`${API_BASE}/conversations/${conversationId}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: AUTH_TOKEN,
                },
                body: JSON.stringify({ user: user?.id }),
            });
        } catch (e) {
            console.error("Delete error:", e);
        } finally {
            fetchAllSessions();
            // If deleting current session, redirect to new chat
            if (conversationId === currentSessionId) {
                window.location.replace(`/aitutor?user_id=${user?.id}&new=1`);
            }
        }
    };

    // ✅ Fixed handleNewChat — navigates without creating a session (session created on first message)
    const handleNewChat = () => {
        window.location.replace(`/aitutor?user_id=${user?.id}&new=1`);
    };

    const formatChatDate = (dateStr) => {
        const date = new Date(typeof dateStr === "number" ? dateStr * 1000 : dateStr);
        const now = new Date();

        const isSameDay = (d1, d2) =>
            d1.getDate() === d2.getDate() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getFullYear() === d2.getFullYear();

        const yesterday = new Date();
        yesterday.setDate(now.getDate() - 1);

        if (isSameDay(date, now)) return "Today";
        if (isSameDay(date, yesterday)) return "Yesterday";

        return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    // ✅ Group sessions by date
    const groupedSessions = useMemo(() => {
        const grouped = sessions.reduce((acc, session) => {
            const date = formatChatDate(session.created_at);
            if (!acc[date]) acc[date] = [];
            acc[date].push(session);
            return acc;
        }, {});

        if (!searchTerm.trim()) return grouped;

        const lowerSearch = searchTerm.toLowerCase();
        const filtered = {};
        Object.entries(grouped).forEach(([date, list]) => {
            const matching = list.filter((s) =>
                (s.name || "").toLowerCase().includes(lowerSearch)
            );
            if (matching.length > 0) filtered[date] = matching;
        });
        return filtered;
    }, [sessions, searchTerm]);

    return (
        <div className="w-72 h-screen bg-primary text-white flex flex-col">

            {/* Account Button */}
            <div className="p-4 pb-0">
                <button className="w-full py-2 px-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded text-white">
                    Go to My Account
                </button>
            </div>

            {/* Header */}
            <div className="p-4 border-b border-white/20">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-6 h-6 bg-white/20 rounded-full" />
                    <span className="font-semibold">Clara AI tutor</span>
                </div>

                <button
                    onClick={handleNewChat}
                    className="py-2 w-full bg-white/10 border border-white/20 text-white rounded hover:bg-white/20"
                >
                    New Chat
                </button>

                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search conversations..."
                    className="px-2 mt-4 py-2 w-full bg-white/10 border border-white/20 text-white placeholder-white/60 rounded"
                />
            </div>

            {/* Session list */}
            <div className="flex-1 overflow-y-auto px-4 py-3 scrollbar-thin scrollbar-thumb-white/20 hover:scrollbar-thumb-white/40">
                <h3 className="text-sm font-medium text-white/80 mb-3">Chat History</h3>

                {Object.entries(groupedSessions).map(([date, list]) => (
                    <div key={date} className="mb-4">
                        <p className="text-xs text-white/50 mb-1">{date}</p>
                        {list.map((item) => (
                            <div
                                key={item.id}
                                onClick={() => handleSessionClick(item.id)}
                                className={`flex items-center justify-between p-2 rounded cursor-pointer mb-1 hover:bg-white/10 transition
                                    ${item.id === currentSessionId ? "bg-white/20" : ""}`}
                            >
                                <span className="truncate text-sm text-white/90 flex-1">{item.name}</span>
                                <button
                                    onClick={(e) => handleDeleteConversation(e, item.id)}
                                    className="ml-2 text-white/50 hover:text-red-400 transition shrink-0"
                                >
                                    <FaTrash size={12} />
                                </button>
                            </div>
                        ))}
                    </div>
                ))}

                {Object.keys(groupedSessions).length === 0 && (
                    <p className="text-sm text-white/40 text-center mt-4">No conversations found</p>
                )}
            </div>

            {/* Bottom actions */}
            <div className="p-4 border-t border-white/20 space-y-2">
                <button onClick={onProfileClick} className="w-full text-left text-white/90 hover:bg-white/10 p-2 rounded flex items-center gap-2">
                    <div className="w-4 h-4 bg-white/40 rounded-full" />
                    Profile
                </button>
                <button onClick={onLogoutClick} className="w-full text-left text-white/90 hover:bg-white/10 p-2 rounded flex items-center gap-2">
                    <div className="w-4 h-4 bg-white/40 rounded-full" />
                    Logout
                </button>
            </div>
        </div>
    );
}

export default ChatSidebar;